import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Entity} from "./entity";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import {SystemProcessArtifactType, toSystemProcessArtifactType} from "./system-process-artifact-type";
import {FileType, toFileType} from "./file-type";
import {FileStorageType, toFileStorageType} from "./file-storage-type";
import {ConnectorType, toConnectorType} from "./connector-type";

export const SystemProcessExecutionArtifactCodec = t.intersection([
  t.type({
    id: t.string,
    systemProcessExecutionId: t.string,
    type: t.string,
    name: t.string,
    fileType: t.string,
    fileName: t.string,
    storageType: t.string,
    storageId: t.string,
  }),
  t.partial({
    storagePath: t.string,
    storageRelativePath: t.string,
    contentLength: t.number,
    contentType: t.string,
    checksumSha256: t.string,
    connectorType: t.string,
    connectorRef: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
    downloadUrl: t.string,
  }),
]);

export type SystemProcessExecutionArtifact = t.TypeOf<typeof SystemProcessExecutionArtifactCodec>;

@Exclude()
export class SystemProcessExecutionArtifactEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  systemProcessExecutionId!: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toSystemProcessArtifactType))
  type!: SystemProcessArtifactType;

  @Expose()
  name!: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toFileType))
  fileType!: FileType;

  @Expose()
  fileName!: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toFileStorageType))
  storageType!: FileStorageType;

  @Expose()
  storageId!: string;

  // ─────────────────────────────
  // Optional fields
  // ─────────────────────────────

  @Expose()
  storagePath?: string;

  @Expose()
  storageRelativePath?: string;

  @Expose()
  contentLength?: number;

  @Expose()
  contentType?: string;

  @Expose()
  checksumSha256?: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toConnectorType))
  connectorType?: ConnectorType;

  @Expose()
  connectorRef?: string;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  @Expose()
  downloadUrl?: string;

  readonly entityType = "SYSPROCEXECARTIFACT";

  get displayName(): Readonly<string> {
    return this.id;
  }

  constructor() {
  }

  static from(value: SystemProcessExecutionArtifact): SystemProcessExecutionArtifactEntity {
    return plainToInstance(SystemProcessExecutionArtifactEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<SystemProcessExecutionArtifact> {
    const object = instanceToPlain(this, {excludeExtraneousValues: true});
    return validateJSON(SystemProcessExecutionArtifactCodec, object);
  }

  asTypeValue(): SystemProcessExecutionArtifact {
    const object = instanceToPlain(this, {excludeExtraneousValues: true});
    return validateJSONNow(SystemProcessExecutionArtifactCodec, object);
  }
}
